import React, { useState } from 'react';
import { View } from 'react-native';
import {
  CURRENT_USER,
  trackRecord,
  skills,
  industries,
  stages,
  resourceOffers,
  resourceNeeds,
  type Profile,
  type Command,
} from '@ventureroom/core';
import { useApp, useTheme, useData, useMe } from './provider';
import {
  Avatar,
  Brand,
  Button,
  Card,
  Column,
  Copy,
  Empty,
  Field,
  FormFields,
  FormSheet,
  FormValues,
  Heading,
  Metric,
  Page,
  Row,
  Tag,
} from './components';
export const profileFields: Field[] = [
  { key: 'name', label: 'Your name', required: true },
  { key: 'bio', label: 'A little about you', type: 'long', required: true },
  { key: 'location', label: 'Broad location', hint: 'e.g. Bristol, UK', required: true },
  {
    key: 'role',
    label: 'I am here as a',
    type: 'select',
    options: ['Skill Provider', 'Business Owner', 'Both'],
    required: true,
  },
  {
    key: 'preference',
    label: 'Where you work',
    type: 'select',
    options: ['Remote', 'Local', 'Hybrid'],
  },
  {
    key: 'background',
    label: 'Professional background',
    hint: 'What have you learned along the way?',
    required: true,
  },
  { key: 'skills', label: 'Your strengths', type: 'multi', options: [...skills], required: true },
  {
    key: 'seeking',
    label: 'What would complement you?',
    type: 'multi',
    options: [...skills],
    required: true,
  },
  {
    key: 'industries',
    label: 'Industries you care about',
    type: 'multi',
    options: [...industries],
    required: true,
  },
  {
    key: 'brings',
    label: 'What you can contribute',
    type: 'multi',
    options: [...resourceOffers],
    required: true,
  },
  {
    key: 'wants',
    label: 'What you’re looking for',
    type: 'multi',
    options: [...resourceNeeds],
    required: true,
  },
  { key: 'hours', label: 'Hours per week', type: 'number', required: true },
  {
    key: 'stage',
    label: 'Preferred venture stage',
    type: 'select',
    options: [...stages].filter((s) => s !== 'Closed' && s !== 'Paused'),
  },
  {
    key: 'style',
    label: 'Working style',
    type: 'select',
    options: ['Async first', 'Regular check-ins', 'Co-working'],
  },
  {
    key: 'commitment',
    label: 'Commitment level',
    type: 'select',
    options: ['Exploring', 'Side project', 'Full time'],
  },
  {
    key: 'idea',
    label: 'Where are you with an idea?',
    type: 'select',
    options: ['Have an idea', 'Join an idea', 'Either'],
  },
  { key: 'teamSize', label: 'Preferred team size', type: 'number', required: true },
  {
    key: 'collaborationBrief',
    label: 'What do you need help with?',
    type: 'long',
    hint: 'Describe your venture or the work you want a collaborator to own.',
  },
];
export function profileValues(p: Profile): FormValues {
  return Object.fromEntries(Object.entries(p).filter(([key]) => key !== 'looking')) as FormValues;
}
export function Welcome() {
  const { enter, startProfile, busy } = useApp();
  const me = useMe();
  const t = useTheme();
  const [step, setStep] = useState(-1);
  const [values, setValues] = useState<FormValues>({
    ...profileValues(me),
    name: '',
    bio: '',
    background: '',
    location: '',
    role: 'Both',
    skills: [],
    seeking: [],
    industries: [],
    brings: [],
    wants: [],
  });
  const [error, setError] = useState('');
  const groups = [profileFields.slice(0, 6), profileFields.slice(6, 11), profileFields.slice(11)];
  async function next() {
    const missing = groups[step]?.find(
      (f) =>
        f.required &&
        (!values[f.key] ||
          (Array.isArray(values[f.key]) && (values[f.key] as string[]).length === 0)),
    );
    if (missing) {
      setError(`Please complete ${missing.label.toLowerCase()}.`);
      return;
    }
    setError('');
    if (step < 2) setStep(step + 1);
    else await startProfile({ ...values, id: CURRENT_USER, looking: true });
  }
  if (step >= 0)
    return (
      <Page
        title={['Your profile', 'Your strengths', 'Your preferences'][step]!}
        back={() => setStep(step - 1)}
      >
        <Row>
          {[0, 1, 2].map((n) => (
            <View
              key={n}
              style={{
                height: 5,
                flex: 1,
                borderRadius: 8,
                backgroundColor: n <= step ? t.accent : t.border,
              }}
            />
          ))}
        </Row>
        <Card>
          <FormFields
            fields={groups[step]!}
            values={values}
            onChange={(k, v) => setValues({ ...values, [k]: v })}
          />
          {!!error && <Copy color={t.danger}>{error}</Copy>}
          <Row between>
            <Button disabled={busy} onPress={() => void next()}>
              {step === 2 ? 'Find my people' : 'Continue →'}
            </Button>
          </Row>
        </Card>
        <Button kind="quiet" onPress={() => void enter()}>
          Skip and enter demo
        </Button>
      </Page>
    );
  return (
    <Page title="">
      <View style={{ maxWidth: 920, width: '100%', alignSelf: 'center', gap: 32 }}>
        <Brand />
        <View
          style={{
            backgroundColor: t.hero,
            borderRadius: 32,
            padding: 32,
            minHeight: 250,
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <Row between wrap>
            <Column gap={20} style={{ flex: 1, minWidth: 220 }}>
              <Tag>GOOD THINGS START WITH PEOPLE</Tag>
              <Heading size={44}>Your next chapter{`\n`}starts with a hello.</Heading>
              <Copy size={17} color={t.heroInk} style={{ maxWidth: 420 }}>
                Find the people who bring what you’re missing. Then build something that matters,
                together.
              </Copy>
            </Column>
            <View
              style={{
                width: 180,
                height: 170,
                alignItems: 'center',
                justifyContent: 'center',
                transform: [{ rotate: '-8deg' }],
              }}
            >
              <Row>
                <Avatar person={{ name: 'You', avatar: 'sand' }} size={86} />
                <Avatar person={{ name: 'Your team', avatar: 'coral' }} size={86} />
              </Row>
              <View style={{ marginTop: -14 }}>
                <Avatar person={{ name: 'New possibilities', avatar: 'sage' }} size={86} />
              </View>
            </View>
          </Row>
        </View>
        <Row wrap>
          <Button onPress={() => setStep(0)}>Create my founder profile →</Button>
          <Button kind="secondary" onPress={() => void enter()}>
            Enter Demo
          </Button>
        </Row>
        <Row wrap style={{ gap: 28 }}>
          {[
            ['01', 'Find your people', 'Complementary strengths. Shared ambition.'],
            ['02', 'Make a little progress', 'One venture, one clear next step.'],
            ['03', 'Keep what you learn', 'Your contribution history stays with you.'],
          ].map(([n, title, detail]) => (
            <Column key={n} gap={6} style={{ flex: 1, minWidth: 200 }}>
              <Copy color={t.accent} bold size={12}>
                {n}
              </Copy>
              <Copy bold>{title}</Copy>
              <Copy muted size={13}>
                {detail}
              </Copy>
            </Column>
          ))}
        </Row>
        <Copy muted size={12}>
          A local beta. Demo profiles are fictional. No external account needed.
        </Copy>
      </View>
    </Page>
  );
}
export function PersonCard({ person }: { person: Profile }) {
  const { navigate } = useApp();
  return (
    <Card
      label={`View ${person.name}'s profile`}
      onPress={() => navigate(`person/${person.id}`)}
      style={{ width: '100%' }}
    >
      <Row>
        <Avatar person={person} size={48} />
        <Column gap={4} style={{ flex: 1 }}>
          <Heading size={20}>{person.name}</Heading>
          <Copy muted size={13}>
            {person.skills.join(' · ')}
          </Copy>
        </Column>
        <Copy muted>›</Copy>
      </Row>
    </Card>
  );
}
export function ProfileScreen({ personId }: { personId?: string }) {
  const data = useData();
  const { navigate, act } = useApp();
  const t = useTheme();
  const person = data.profiles.find((p) => p.id === (personId ?? CURRENT_USER));
  const [edit, setEdit] = useState(false);
  if (!person)
    return (
      <Page title="Founder unavailable" back="discover">
        <Empty
          title="We couldn’t find this person"
          detail="Return to Discover to meet the other founders."
          action={<Button onPress={() => navigate('discover')}>Discover founders</Button>}
        />
      </Page>
    );
  const self = person.id === CURRENT_USER,
    record = trackRecord(data, person.id);

  return (
    <Page title="Profile" back={personId ? 'discover' : undefined}>
      <Column gap={18}>
        <Row>
          <Avatar person={person} size={72} />
          <Column gap={4} style={{ flex: 1 }}>
            <Row style={{ alignItems: 'center', gap: 8 }}>
              <Heading size={24}>{person.name}</Heading>
              {person.role === 'Skill Provider' && person.subscriptionTier === 'agency' && (
                <View
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 12,
                    borderWidth: 2,
                    borderColor: '#800020',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Copy style={{ color: '#800020', fontWeight: 'bold', fontSize: 14 }}>
                    {'\u2714'}
                  </Copy>
                </View>
              )}
            </Row>
            {person.is_ai && (
              <Copy muted size={12}>
                AI Collaborator · {person.ai?.availability}
              </Copy>
            )}
            <Copy muted size={13}>
              {person.background}
            </Copy>
            <Copy muted size={12}>
              {person.location}
            </Copy>
          </Column>
        </Row>
        <Copy size={17}>{person.bio}</Copy>
        <Copy muted>{person.skills.join(' · ')}</Copy>
        {self && (
          <Row style={{ justifyContent: 'center' }}>
            <Button onPress={() => setEdit(true)}>Edit profile</Button>
          </Row>
        )}
      </Column>
      <View style={{ backgroundColor: t.surfaceAlt, borderRadius: 24, padding: 24, marginTop: 12 }}>
        <View style={{ marginBottom: 12 }}>
          <Heading size={18}>What I Bring</Heading>
        </View>
        <Row wrap style={{ marginBottom: 24 }}>
          {Array.from(
            new Set([...person.skills, ...(Array.isArray(person.brings) ? person.brings : [])]),
          ).map((skill) => (
            <View
              key={skill}
              style={{
                backgroundColor: t.accent,
                paddingHorizontal: 14,
                paddingVertical: 8,
                borderRadius: 100,
              }}
            >
              <Copy size={14} bold color={t.onAccent}>
                {skill}
              </Copy>
            </View>
          ))}
        </Row>

        <View style={{ marginBottom: 12 }}>
          <Heading size={18}>What I Need</Heading>
        </View>
        <Row wrap>
          {person.seeking.map((seek) => (
            <View
              key={seek}
              style={{
                backgroundColor: t.accent,
                paddingHorizontal: 14,
                paddingVertical: 8,
                borderRadius: 100,
              }}
            >
              <Copy size={14} bold color={t.onAccent}>
                {seek}
              </Copy>
            </View>
          ))}
          {Array.isArray(person.wants) &&
            person.wants.map((want) => (
              <View
                key={want}
                style={{
                  backgroundColor: t.accent,
                  paddingHorizontal: 14,
                  paddingVertical: 8,
                  borderRadius: 100,
                }}
              >
                <Copy size={14} bold color={t.onAccent}>
                  {want}
                </Copy>
              </View>
            ))}
        </Row>
      </View>

      <View style={{ backgroundColor: t.surfaceAlt, borderRadius: 24, padding: 24, marginTop: 12 }}>
        <View style={{ marginBottom: 16 }}>
          <Heading size={18}>Stats</Heading>
        </View>
        <Row wrap>
          <Metric value={record.ventures.length} label="ventures" />
          <Metric value={record.count} label="contributions" />
        </Row>
      </View>
      {edit && (
        <FormSheet
          title="Edit your founder profile"
          fields={profileFields}
          initial={profileValues(person)}
          onClose={() => setEdit(false)}
          onSave={(v) =>
            act(
              { type: 'profile', profile: { ...person, ...v } } as Command,
              'Founder profile updated',
            )
          }
        />
      )}
    </Page>
  );
}
